import Fuse from 'fuse.js';
import { navigation, downloads } from '../constants';
import { creditsData } from '../pages/CreditsData';
import { unsortedPosters, servicesData, experienceHighlights } from '../constants/homepage';

export interface SearchResult {
    title: string;
    description: string;
    url: string;
    category: string;
    type: 'page' | 'tool' | 'download' | 'project' | 'content';
}

const getSearchIndex = (): SearchResult[] => {
    const index: SearchResult[] = [];

    // 1. Navigation Pages
    navigation.forEach(item => {
        if (item.href !== '#') {
            index.push({
                title: item.name,
                description: `Navigate to the ${item.name} page.`,
                url: item.href,
                category: 'Pages',
                type: 'page'
            });
        }
        if (item.children) {
            item.children.forEach(child => {
                index.push({
                    title: child.name,
                    description: `Utility tool: ${child.name}`,
                    url: child.href,
                    category: 'Tools',
                    type: 'tool'
                });
            });
        }
    });

    // 2. Downloads
    downloads.forEach(dl => {
        index.push({
            title: dl.name,
            description: dl.description,
            url: '/downloads', // Link to the downloads page or direct link if preferred
            category: 'Downloads',
            type: 'download'
        });
    });

    // 3. Credits Projects
    creditsData.forEach(yearGroup => {
        yearGroup.projects.forEach(project => {
            index.push({
                title: project.title,
                description: `${project.role} | Directed by ${project.director} | ${project.details}`,
                url: '/credits',
                category: 'Projects',
                type: 'project'
            });
        });
    });

    // 4. Homepage Content
    unsortedPosters.forEach(poster => {
        index.push({
            title: poster.title,
            description: `Film project featured on homepage.`,
            url: '/',
            category: 'Featured Work',
            type: 'project'
        });
    });

    servicesData.forEach(service => {
        index.push({
            title: service.title,
            description: service.text,
            url: '/',
            category: 'Services',
            type: 'content'
        });
    });

    experienceHighlights.forEach(highlight => {
        index.push({
            title: highlight.title,
            description: `${highlight.description} (${highlight.metric})`,
            url: '/',
            category: 'Expertise',
            type: 'content'
        });
    });

    // 5. Specific Content from App Pages (Manual Indexing for deep links)

    // Cinemmerse Pro deep features
    index.push({
        title: 'Cinemmerse Pro Features',
        description: 'Lossless Transparency, Bit-transparent conversion using SMPTE DLC, Cinematic Aurora UI, 128-channel monitoring.',
        url: '/cinemmerse-pro',
        category: 'Tools',
        type: 'content'
    });

    // SoundLibraryExplorer deep features
    index.push({
        title: 'SoundLibraryExplorer Features',
        description: 'Smart audio organization, automatic tagging, WAV, MP3, AIFF, FLAC support, Visual waveform preview.',
        url: '/sound-library-explorer',
        category: 'Tools',
        type: 'content'
    });

    // Timecode Notes
    index.push({
        title: 'Timecode Notes App',
        description: 'Professional timecode logging and notes tool for film and audio post-production.',
        url: '/timecode-notes',
        category: 'Tools',
        type: 'tool'
    });

    // DCP Tool
    index.push({
        title: 'DCP Name Generator',
        description: 'DCI-compliant DCP naming convention generator for theatrical delivery.',
        url: '/dcp-tool',
        category: 'Tools',
        type: 'tool'
    });

    // Music Page Content
    index.push({
        title: 'PushpaSangam Music',
        description: 'Musical journey and compositions by Sangam Panta. Explore Spotify and YouTube playlists.',
        url: '/music',
        category: 'Music',
        type: 'content'
    });

    return index;
};

const searchIndex = getSearchIndex();

const fuseOptions = {
    keys: [
        { name: 'title', weight: 0.7 },
        { name: 'description', weight: 0.3 }
    ],
    threshold: 0.3,
    distance: 100,
    ignoreLocation: true,
    includeMatches: true,
    minMatchCharLength: 2
};

const fuse = new Fuse(searchIndex, fuseOptions);

export const searchSite = (query: string) => {
    if (!query) return [];
    return fuse.search(query).map((result: any) => ({
        ...result.item,
        matches: result.matches
    }));
};
